import { Order, OrderItem, Owner, OwnerType } from "@/api/SolarCloudApi";
import { formatPrice } from "@/util/formatPrice";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { useUser } from "@/queries/user/useUser";

type OrderSummaryProps = {
  order: Order;
  className?: string;
  summaryLabel?: string;
  summaryAmount?: number;
};

export default function OrderSummary({
  order,
  className,
  summaryLabel = order.billingOption === "recurring" ? "First payment" : "Deposit",
  summaryAmount,
}: OrderSummaryProps) {
  const { data: user } = useUser();
  const isDiscounted = order.totalDiscount! > 0;
  const formattedFullPrice = formatPrice(order.fullPrice || 0);
  const formattedTotalPrice = formatPrice(order.totalPrice || 0);
  const formattedTotalHold = formatPrice(order.totalHold || 0);
  const primaryAmount = summaryAmount ?? (order.billingOption === "recurring" ? (order.totalPrice || 0) : (order.totalDeposit || 0));
  const formattedPrimaryAmount = formatPrice(primaryAmount);
  const totalPrimaryLabel = `Total ${summaryLabel}`;

  let owner: Owner;
  if (order.owner?.type === OwnerType.ORGANISATION) {
    const org = user?.organisations?.find(
      (org) => org.uuid === order.owner?.organisation?.uuid
    );
    owner = {
      ...order.owner,
      organisation: org,
    };
  } else {
    owner = {
      ...order.owner,
      user: user,
    };
  }

  return (
    <Accordion type="single" collapsible className="px-6 ">
      <AccordionItem
        value="item-1"
        className="border-b border-brand-neutral-700"
      >
        <AccordionTrigger className="w-full flex justify-between">
          <span className="font-semibold leading-none flex-1 text-left">
            Order Summary
          </span>
          <span>{summaryLabel}: </span>
          <span className="px-2">{formattedPrimaryAmount}</span>
        </AccordionTrigger>
        <AccordionContent>
          <p className="text-muted-foreground text-sm mb-4">
            Here is a summary of the virtual panels you have selected.
          </p>

          <ul className="flex flex-col gap-4 flex-1 text-foreground text-sm">
            {order.items?.map((item: OrderItem, index: number) => (
              <ItemSummary key={item.uuid ?? `item-${index}`} item={item} />
            ))}
          </ul>
          <ul className="flex flex-col gap-2 mt-4 border-t border-b border-brand-orange-500 py-2">
            {isDiscounted && (
              <li className="flex justify-between ml-auto text-foreground">
                <span>RRP:</span>
                <span className="min-w-20 text-end line-through">
                  {formattedFullPrice}
                </span>
              </li>
            )}
            <li className="flex justify-between ml-auto text-foreground">
              <span>Total Price:</span>
              <span className="min-w-20 text-end">{formattedTotalPrice}</span>
            </li>
            <li className="flex justify-between ml-auto text-foreground">
              <span>Balance:</span>
              <span className="min-w-20 text-end">{formattedTotalHold}</span>
            </li>
            <div className="h-[1px] w-full bg-brand-neutral-650" />
            <li className="flex justify-between ml-auto font-bold tracking-none">
              <span>{totalPrimaryLabel}:</span>
              <span className="min-w-20 text-end">{formattedPrimaryAmount}</span>
            </li>
          </ul>
          {order.billingOption === "recurring" && (
            <div className="flex flex-col gap-1 mt-4 text-sm text-muted-foreground border-t border-brand-neutral-700 pt-4">
              <div className="flex justify-between">
                <span>Frequency:</span>
                <span className="capitalize font-medium text-foreground">{order.recurringFrequency}</span>
              </div>
              <div className="flex justify-between">
                <span>Quantity:</span>
                <span className="font-medium text-foreground">
                  {order.items?.reduce((sum, item) => {
                    const q = (item.units ?? 0) / (item.product?.wattPanelSize || 100);
                    return sum + q;
                  }, 0)}{" "}
                  {order.items?.reduce((sum, item) => sum + (item.units ?? 0) / (item.product?.wattPanelSize || 100), 0) === 1 ? "panel" : "panels"}
                </span>
              </div>
              {order.recurringPaymentCount && (
                <div className="flex justify-between">
                  <span>Duration:</span>
                  <span className="font-medium text-foreground">
                    {order.recurringPaymentCount} {
                      order.recurringFrequency?.toLowerCase() === "weekly" ? (order.recurringPaymentCount === 1 ? "week" : "weeks") : 
                      order.recurringFrequency?.toLowerCase() === "fortnightly" || order.recurringFrequency === "Every 2 weeks" ? (order.recurringPaymentCount === 1 ? "fortnight" : "fortnights") :
                      order.recurringFrequency?.toLowerCase() === "monthly" ? (order.recurringPaymentCount === 1 ? "month" : "months") : "payments"
                    }
                  </span>
                </div>
              )}
            </div>
          )}
          <p className="text-sm text-muted-foreground mt-4">
            Order for:
            {owner.type === OwnerType.USER
              ? ` ${owner.user?.givenName} ${owner.user?.familyName}`
              : ` ${owner.organisation?.name}, ${formatAbn(
                  owner.organisation?.abn
                )}`}
          </p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

type ItemSummaryProps = {
  item: OrderItem;
};

function ItemSummary({ item }: ItemSummaryProps) {
  const formattedItemPrice = formatPrice(item.itemFullPrice || 0);
  const quantity = item?.units! / (item.product?.wattPanelSize || 1);

  return (
    <li className="gap-4 w-full flex py-2">
      <p className="w-10">{quantity}x</p>
      <h3 className="flex-1">{item.product?.name}</h3>
      <p className="text-end min-w-24">{formattedItemPrice}</p>
    </li>
  );
}

function formatAbn(abn?: string | null): string {
  if (!abn) return "";
  const digitsOnly = abn.replace(/\D/g, ""); // remove non-numeric characters
  return digitsOnly.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3 $4");
}
